import { useTranslation } from 'react-i18next'
import Button from '@/shared/components/Button/Button'
import { Row } from '../../schema/document'
import { DerivedPalette } from '../../render/palette'
import styles from './BackgroundPicker.module.scss'

interface BackgroundPickerProps {
  value: Row['background']
  /** The colours actually in force, so a swatch shows the real thing. */
  palette: DerivedPalette
  onChange: (background: Row['background']) => void
}

/**
 * Picking a row's background.
 *
 * It used to be a dropdown reading "none / light / muted / highlighted". Those
 * are the names of ROLES, which is how the document stores them and how the
 * palette keeps a page coherent, but they are meaningless to the person
 * choosing: even the product owner could not say what "highlighted" was. The
 * roles are still what gets saved. They are simply no longer what gets read.
 *
 * Two options, not four. Measured, the four differed by contrast ratios of 1.15
 * to 1.40 against each other, where 1.0 is the same colour: they were not four
 * choices, they were one choice printed four times. A section either sits on the
 * page or is picked out from it.
 *
 * Two shared `Button`s, chosen one filled and unchosen one outlined, which is
 * how the floor planner's snap toggle says the same thing. It was a pair of
 * hand-built tiles that restated the app's own button down to its focus ring,
 * which is the private copy §3 forbids.
 */
function BackgroundPicker(props: BackgroundPickerProps) {
  const { value, palette, onChange } = props
  const { t } = useTranslation()

  // Any colour slot reads as "highlighted", so a row saved with one of the older
  // slots still shows as chosen rather than as nothing.
  const isHighlighted = value.type === 'color'

  /* Computed value, so it cannot be a class. */
  const swatch = (colour: string) => (
    <span className={styles.swatch} style={{ backgroundColor: colour }} />
  )

  return (
    <div className={styles.field}>
      <p className={`label-bold-medium ${styles.label}`}>
        {t('WEBSITE.PROPS.BACKGROUND')}
      </p>

      <div className={styles.row}>
        <Button
          label={t('WEBSITE.BACKGROUND.plain')}
          variant={isHighlighted ? 'outlined' : 'contained'}
          color="primary"
          icon={swatch(palette.bg)}
          iconPosition="end"
          textAlignment="left"
          maxWidth
          smallFont
          onClick={() => onChange({ type: 'none' })}
        />
        <Button
          label={t('WEBSITE.BACKGROUND.highlighted')}
          variant={isHighlighted ? 'contained' : 'outlined'}
          color="primary"
          icon={swatch(palette.accent)}
          iconPosition="end"
          textAlignment="left"
          maxWidth
          smallFont
          onClick={() => onChange({ type: 'color', slot: 'accent' })}
        />
      </div>
    </div>
  )
}

export default BackgroundPicker
